import { categoryApi } from '@/api/services/categoryApi';
import { NumericTextBox, TextBox } from '@/components/ui';
import { createChinaAppSysGroupCategory, type ChinaAppSysGroupCategory } from '@/types/entities';

import { useAdminChanges } from '../adminChanges';
import { AdminNotice } from '../AdminNotice';
import { AdminCategoryViewsList } from '../lists/AdminCategoryViewsList';
import { AdminFormFrame } from './AdminFormFrame';
import { useAdminForm } from './useAdminForm';

/** Port von `IwmAdminKategorieForm.razor` in der Gestalt aus REQ-25 §4.6 (REQ-28 §4). */
export interface AdminCategoryFormProps {
  /** `0` legt eine neue Kategorie an. */
  id: number;
  onSave: (id: number) => void | Promise<void>;
}

const fetch = (id: number) => categoryApi.getEntry(id);
const create = (model: ChinaAppSysGroupCategory) => categoryApi.create(model);
const put = (id: number, model: ChinaAppSysGroupCategory) => categoryApi.put(id, model);
const remove = (id: number) => categoryApi.remove(id);

export function AdminCategoryForm(props: AdminCategoryFormProps) {
  const changes = useAdminChanges();
  const form = useAdminForm({
    id: props.id,
    createEmpty: createChinaAppSysGroupCategory,
    fetch,
    create,
    put,
    remove,
    deleteMessage:
      'Möchten Sie diese Kategorie wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
    deleteTitle: 'Kategorie löschen',
    // Kategorie und Menüsortierung stehen in der Navigation — sie zieht nach.
    onSave: async (id) => {
      await props.onSave(id);
      changes.notifyNavigationChanged();
    },
  });
  const { model } = form;
  const existing = model.Id > 0;

  return (
    <AdminFormFrame
      title={existing ? (model.Bezeichnung ?? undefined) : undefined}
      showDelete={existing}
      saving={form.saving}
      isValid={form.isValid}
      dirty={form.dirty}
      onNew={form.onNew}
      onRefresh={() => form.reload()}
      onDelete={form.onDelete}
      onSave={form.onSave}
    >
      <div className="iwm-admin-form-grid">
        <TextBox
          value={model.Bezeichnung}
          onChange={(value) => {
            form.setField('Bezeichnung', value);
          }}
          label="Name"
        />
        <NumericTextBox
          value={model.MenuSortierung}
          onChange={(value) => {
            form.setField('MenuSortierung', value ?? 0);
          }}
          label="Menu Order"
          hint="Kleinere Zahlen stehen in der Navigation weiter vorn."
          format="d"
          min={0}
        />
      </div>
      {existing ? (
        <AdminCategoryViewsList parentId={model.Id} />
      ) : (
        <AdminNotice>
          Views lassen sich erst zuordnen, wenn die Kategorie gespeichert ist.
        </AdminNotice>
      )}
    </AdminFormFrame>
  );
}

export default AdminCategoryForm;
